import React, { useEffect, useState } from 'react';
import {
  useGetEventsCalendarStatusQuery,
  useGetEventsCalendarSettingsQuery,
  useSetEventsCalendarSettingsMutation,
} from '../app/api';

const brand = {
  deepBlue:  '#0a2947',
  tealBlue:  '#0069a6',
  goldenrod: '#d09b2c',
  charcoal:  '#58585b',
  softGray:  '#e9e6e1',
  green:     '#1a7f4e',
  red:       '#c0392b',
};

const inputStyle = {
  padding: '6px 10px', border: `1px solid ${brand.softGray}`,
  borderRadius: 6, fontSize: '0.85rem', color: brand.deepBlue, outline: 'none', background: 'white',
};

// Which shared Outlook mailbox holds the firm's events calendar. Blank hides the
// Upcoming Events menu row for everyone. Setting it grants nobody anything:
// each employee's read goes through their own Microsoft token, so Exchange
// still has to let them open that calendar.
export default function EventsCalendarSection() {
  const { data, isLoading } = useGetEventsCalendarSettingsQuery();
  const [save, { isLoading: saving }] = useSetEventsCalendarSettingsMutation();
  // Shares the sidebar's cache entry, so refetching it here shows or hides the
  // Upcoming Events row straight away.
  const { refetch: refetchStatus } = useGetEventsCalendarStatusQuery();
  const [mailbox, setMailbox] = useState('');
  const [error, setError] = useState(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (data) setMailbox(data.mailbox || '');
  }, [data]);

  const onSave = async (e) => {
    e.preventDefault();
    setError(null); setSaved(false);
    try {
      const res = await save({ mailbox: mailbox.trim() }).unwrap();
      setMailbox(res.mailbox || '');
      setSaved(true);
      refetchStatus();
    } catch (err) {
      setError(err?.data?.detail || 'Could not save the events calendar.');
    }
  };

  return (
    <div style={{ background: 'white', borderRadius: 12, boxShadow: '0 2px 12px rgba(10,41,71,0.08)', padding: '1.25rem 1.5rem', marginBottom: '1.5rem' }}>
      <h5 style={{ color: brand.deepBlue, fontWeight: 800, margin: '0 0 0.25rem' }}>
        <i className="bi bi-calendar-event me-2" />Events calendar
      </h5>
      <p style={{ color: brand.charcoal, fontSize: '0.85rem', margin: '0 0 0.5rem' }}>
        The shared Outlook mailbox whose calendar appears as <strong>Upcoming Events</strong> in
        every employee&rsquo;s HR menu. Leave it blank to hide the page.
      </p>
      <p style={{ color: brand.charcoal, fontSize: '0.85rem', margin: '0 0 1rem' }}>
        Employees read it with their own Microsoft sign-in, so everyone needs read access to that
        calendar in Outlook. Anyone without it sees a message asking them to contact IT.
      </p>
      {isLoading ? (
        <div className="spinner-border spinner-border-sm text-primary" role="status" />
      ) : (
        <form onSubmit={onSave} style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
          <input
            type="email"
            value={mailbox}
            onChange={(e) => { setMailbox(e.target.value); setSaved(false); }}
            placeholder="companycalendar@prosperitycapitaladvisors.com"
            aria-label="Events calendar mailbox"
            style={{ ...inputStyle, flex: '1 1 260px', maxWidth: 380 }}
          />
          <button
            type="submit"
            disabled={saving}
            style={{
              background: brand.deepBlue, color: 'white', border: 'none', borderRadius: 6,
              padding: '6px 14px', fontWeight: 600, fontSize: '0.85rem', opacity: saving ? 0.7 : 1,
            }}
          >
            {saving ? 'Saving…' : 'Save'}
          </button>
          {saved && <span style={{ color: brand.green, fontSize: '0.85rem' }}><i className="bi bi-check-circle-fill me-1" />Saved</span>}
        </form>
      )}
      {error && (
        <div style={{ marginTop: '0.6rem', color: brand.red, fontSize: '0.85rem' }}>
          <i className="bi bi-exclamation-circle me-1" />{error}
        </div>
      )}
    </div>
  );
}
